import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PersonAdd as AddUserIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useGetIdentity } from 'react-admin';
import { PageTitle } from '../components/PageTitle';
import { tenantService } from '../services/tenantService';

const roleOptions = [
  { value: 'user', label: 'User' },
  { value: 'operator', label: 'Operator' },
  { value: 'admin', label: 'Admin' },
];

export const UserCreatePage = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const tenantCode = tenantService.getCurrentTenantCode();

  const [step, setStep] = useState<'email' | 'details'>('email');
  const [userStatus, setUserStatus] = useState<{
    exists: boolean;
    inTenant: boolean;
    user: any;
    tenantUser: any;
  } | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    temporaryPassword: '',
    roles: ['user'],
    sendInvitation: true,
  });

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isDistillUser = identity?.email?.endsWith('@distillmobile.com');

  // Email validation
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const emailError = formData.email && !isValidEmail(formData.email) ? 'Please enter a valid email address' : '';

  const handleEmailSubmit = async (email: string) => {
    if (!email.trim() || !isValidEmail(email)) {
      notify('Please enter a valid email address', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const response = await dataProvider.getOne('tenant-users/check-user', {
        id: encodeURIComponent(email.toLowerCase()),
      });

      setUserStatus(response.data);
      setFormData(prev => ({
        ...prev,
        email: email.toLowerCase(),
        name: response.data.user?.name || prev.name,
      }));
      setStep('details');
    } catch (error: any) {
      console.error('Error checking user status:', error);
      const message = error?.message || 'Error checking user status';
      notify(message, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setUserStatus(null);
    setFormData(prev => ({ ...prev, email: '', name: '', temporaryPassword: '' }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);

    try {
      const submitData: any = {
        email: formData.email,
        roles: formData.roles,
        sendInvitation: formData.sendInvitation,
      };

      // Only include name and password for new users
      if (!userStatus?.exists) {
        submitData.name = formData.name;
        submitData.temporaryPassword = formData.temporaryPassword;
      }

      await dataProvider.create('tenant-users', {
        data: submitData
      });

      const action = userStatus?.exists ? 'added to tenant' : 'invited';
      notify(`User ${action} successfully`, { type: 'success' });
      navigate('/users');
    } catch (error: any) {
      const message = error?.body?.message || 'Error processing user invitation';
      notify(message, { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        title="Invite User"
        subtitle="Add a new user to your tenant"
        icon={<AddUserIcon />}
      />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/users')}
            sx={{ mr: 2 }}
          >
            Back to Users
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          {step === 'email' ? (
            // Step 1: Email Input
            <>
              <Typography variant="h6" gutterBottom>
                Enter User Email
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                Enter an email address to check if the user exists and invite them to your tenant.
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  fullWidth
                  required
                  error={!!emailError}
                  helperText={emailError || "Enter the email address of the user you want to invite"}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && isValidEmail(formData.email)) {
                      e.preventDefault();
                      handleEmailSubmit(formData.email);
                    }
                  }}
                />

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    onClick={() => navigate('/users')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleEmailSubmit(formData.email)}
                    variant="contained"
                    disabled={loading || !formData.email.trim() || !isValidEmail(formData.email)}
                    sx={{ backgroundColor: '#1C5195' }}
                  >
                    {loading ? 'Checking...' : 'Continue'}
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            // Step 2: User Details Based on Status
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button
                  startIcon={<BackIcon />}
                  onClick={handleBackToEmail}
                  size="small"
                  sx={{ mr: 2 }}
                >
                  Change Email
                </Button>
                <Typography variant="h6">
                  {userStatus?.exists ? 'Add Existing User' : 'Create New User'}
                </Typography>
              </Box>

              {/* User Status Alert */}
              {userStatus?.inTenant ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{formData.email}</strong> is already a member of this tenant.
                  </Typography>
                  <Typography variant="caption">
                    They currently have the roles: {userStatus.tenantUser?.roles?.join(', ')}
                  </Typography>
                </Alert>
              ) : userStatus?.exists ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>{userStatus.user.name || formData.email}</strong> already has an account.
                  </Typography>
                  <Typography variant="caption">
                    They will be added to this tenant and receive a notification email.
                  </Typography>
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    Creating a new account for <strong>{formData.email}</strong>
                  </Typography>
                  <Typography variant="caption">
                    They will receive an email with their login credentials.
                  </Typography>
                </Alert>
              )}

              {/* Show form only if user is not already in tenant */}
              {!userStatus?.inTenant && (
                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Email (readonly) */}
                    <TextField
                      label="Email"
                      value={formData.email}
                      fullWidth
                      disabled
                      helperText="Email address (confirmed)"
                    />

                    {/* Name - only for new users */}
                    {!userStatus?.exists && (
                      <TextField
                        label="Full Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth
                        required
                        helperText="User's display name"
                      />
                    )}

                    {/* Existing user shows their name (readonly) */}
                    {userStatus?.exists && (
                      <TextField
                        label="Name"
                        value={userStatus.user.name || 'No name set'}
                        fullWidth
                        disabled
                        helperText="Existing user's name"
                      />
                    )}

                    {/* Password - only for new users */}
                    {!userStatus?.exists && (
                      <TextField
                        label="Temporary Password"
                        type="password"
                        value={formData.temporaryPassword}
                        onChange={(e) => setFormData(prev => ({ ...prev, temporaryPassword: e.target.value }))}
                        fullWidth
                        required
                        inputProps={{ minLength: 6 }}
                        helperText="Temporary password (minimum 6 characters). User can change this after logging in."
                      />
                    )}

                    {/* Roles */}
                    <FormControl fullWidth>
                      <InputLabel>Roles</InputLabel>
                      <Select
                        multiple
                        value={formData.roles}
                        onChange={(e) => setFormData(prev => ({ ...prev, roles: e.target.value as string[] }))}
                        input={<OutlinedInput label="Roles" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                        required
                      >
                        {roleOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Send Invitation */}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.sendInvitation}
                          onChange={(e) => setFormData(prev => ({ ...prev, sendInvitation: e.target.checked }))}
                        />
                      }
                      label={`Send ${userStatus?.exists ? 'notification' : 'invitation'} email`}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        type="button"
                        onClick={() => navigate('/users')}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={submitting || (!userStatus?.exists && !formData.temporaryPassword)}
                        sx={{ backgroundColor: '#1C5195' }}
                      >
                        {submitting ? 'Processing...' : userStatus?.exists ? 'Add to Tenant' : 'Create & Invite'}
                      </Button>
                    </Box>
                  </Box>
                </form>
              )}

              {/* If user already in tenant, show back button */}
              {userStatus?.inTenant && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    onClick={() => navigate('/users')}
                    variant="contained"
                    sx={{ backgroundColor: '#1C5195' }}
                  >
                    Back to Users
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};